import axios from 'axios';
import qs from 'qs';
import newRelic from '../helpers/newRelic';
import { allowedRegistrationWorkflowType, WORKFLOW_TYPE_REGISTRATION } from '../helpers/registrationWorkflow';

const identityClientConfig = {
  headers: {
    'Content-Type': 'application/json',
  },
};

const identityAuthCodeFlowConfig = {
  headers: {
    'Content-Type': 'application/json',
  },
};

const identityClient = {
  async setAccountPreference(preference, value, queryString) {
    try {
      await axios.post(
        `/v2/oauth2/setaccountpreference${queryString}`,
        { preference, value },
        identityClientConfig,
      );
    } catch (err) {
      newRelic.noticeError(err);
      throw err;
    }
  },

  async addPhoneNumber(phone, queryString) {
    try {
      await axios.post(
        `/v2/oauth2/addphonenumber${queryString}`,
        { phone },
        identityClientConfig,
      );
      return;
    } catch (err) {
      newRelic.noticeError(err);
      throw err;
    }
  },

  async sendVerificationCode(phone, email, verificationMethod) {
    try {
      const resp = await this.getCurrentSession();
      identityClientConfig.headers['Authorization'] = resp.data.accessToken;
      await axios.post(
        '/api/sendVerification',
        {
          message: {
            phone,
            verificationMethod,
            email,
            hstreamId: resp.data.hstreamId,
          },
        },
        identityClientConfig,
      );
      return;
    } catch (err) {
      newRelic.noticeError(err);
      throw err;
    }
  },

  async verifyPhone(username, verification) {
    const payload = {
      hstreamId: username,
      code: verification,
    };
    try {
      await axios.post(
        `/api/confirm`,
        JSON.stringify(payload),
        identityClientConfig,
      );
      return;
    } catch (err) {
      newRelic.noticeError(err);
      throw err;
    }
  },

  async myAccount(queryString) {
    try {
      const response = await axios.get(
        `/v2/oauth2/getAccount${queryString}`,
        identityClientConfig,
      );
      return response.data;
    } catch (err) {
      newRelic.noticeError(err);
      throw err;
    }
  },

  async getAccountPreferences(queryString) {
    try {
      const response = await axios.get(
        `/v2/oauth2/getaccountpreferences${queryString}`,
        identityClientConfig,
      );
      return response.data.preferences;
    } catch (err) {
      newRelic.noticeError(err);
      throw err;
    }
  },

  async getAccountInfo(hstreamId) {
    try {
      const response = await axios.get(`/api/${hstreamId}/getAccount`, {
        ...identityAuthCodeFlowConfig,
        ...{ withCredentials: true },
      });
      return response.data;
    } catch (err) {
      newRelic.noticeError(err);
      throw err;
    }
  },

  async oauthGetLogin(queryString) {
    const response = await axios.get(`/v2/Oauth2/Login${queryString}`, {
      ...identityAuthCodeFlowConfig,
      ...{ withCredentials: true },
    });
    identityAuthCodeFlowConfig.headers['x-xsrf-token'] =
      response.headers['x-xsrf-token'];
    return response;
  },

  async blindLogin(queryString) {
    try {
      const resp = await axios.post(
        `/v2/Oauth2/ValidateUsername${queryString}`,
        qs.stringify({
          username: null,
        }),
        {
          ...identityAuthCodeFlowConfig,
          ...{
            withCredentials: true,
            headers: {
              ...identityAuthCodeFlowConfig.headers,
              ...{ 'Content-Type': 'application/x-www-form-urlencoded' },
            },
          },
        },
      );
      window.location = resp.headers.location;
      return;
    } catch (err) {
      if (
        err.response &&
        (err.response.status === 401 ||
          err.response.status === 405 ||
          err.response.status === 400)
      ) {
        // if we fail due to no valid cookie, we want to indicate to continue
        // with cookie retrieval
        return;
      }
      // this will get caught and logged by the calling method
      throw err;
    }
  },

  async validateUsername(username, queryString) {
    try {
      const resp = await axios.post(
        `/v2/oauth2/ValidateUsername${queryString}`,
        qs.stringify({
          username: username,
        }),
        {
          ...{
            withCredentials: true,
            headers: {
              ...identityAuthCodeFlowConfig.headers,
              ...{ 'Content-Type': 'application/x-www-form-urlencoded' },
            },
          },
        },
      );

      if (resp.headers.location) {
        window.location = resp.headers.location;
      }

      return resp.data;
    } catch (err) {
      throw err;
    }
  },

  async preRegistrationValidation(email, queryString, workflowType) {
    try {
      const body = { email };
      const allowedWorkflowType = allowedRegistrationWorkflowType(workflowType);
      if (allowedWorkflowType) {
        body.workflow_Type = allowedWorkflowType;
      }
      const resp = await axios.post(
        `/v2/oauth2/RegistrationValidation${queryString}`,
        body,
        {
          ...{
            withCredentials: true,
            headers: {
              ...identityAuthCodeFlowConfig.headers,
              ...{ 'Content-Type': 'application/x-www-form-urlencoded' },
            },
          },
        },
      );

      if (resp.headers.location) {
        window.location = resp.headers.location;
      }

      return resp;
    } catch (err) {
      throw err;
    }
  },

  async getAccountByPhoneNumber(phoneNumber) {
    try {
      const body = { phoneNumber };
      const response = await axios.post(
        '/api/getAccountByPhoneNumber',
        body,
        {
          withCredentials: true,
          headers: {
            ...identityAuthCodeFlowConfig.headers,
            'Content-Type': 'application/json',
          },
        },
      );
      return response.data;
    } catch (err) {
      throw err;
    }
  },

  async checkPasswordReuse(hstreamId, password, email) {
    try {
      const body = { password };
      if (hstreamId) body.hstreamId = hstreamId;
      if (email) body.email = email;
      const response = await axios.post(
        '/api/check-password-reuse',
        body,
        {
          withCredentials: true,
          headers: {
            ...identityAuthCodeFlowConfig.headers,
            'Content-Type': 'application/json',
          },
        },
      );
      return response;
    } catch (err) {
      throw err;
    }
  },

  async sendRegistrationOTP(email, hStreamId, phone, workflowType) {
    try {
      const message = {};
      // Backend rejects requests that include BOTH keys, even if one is empty.
      if (email) message.email = email;
      if (hStreamId) message.hStreamId = hStreamId;
      if (phone) message.phone = phone;
      const allowedWorkflowType = allowedRegistrationWorkflowType(workflowType);
      if (allowedWorkflowType) message.workflowType = allowedWorkflowType;

      const resp = await axios.post(
        '/api/auth/register/otp/send',
        { message },
        {
          withCredentials: true,
          headers: {
            ...identityAuthCodeFlowConfig.headers,
            'Content-Type': 'application/json',
          },
        },
      );

      if (resp.headers.location) {
        window.location = resp.headers.location;
      }

      return resp;
    } catch (err) {
      throw err;
    }
  },

  async verifyRegistrationOtp(
    hStreamId,
    email,
    code,
    phone,
    workflowType,
    allowEmptyVerifyResponse,
  ) {
    try {
      const payload = { code };
      if (hStreamId) payload.hStreamId = hStreamId;
      if (phone) {
        payload.phone = phone;
        if (email) payload.email = email;
      } else {
        payload.email = email;
      }
      const allowedWorkflowType = allowedRegistrationWorkflowType(workflowType);
      if (allowedWorkflowType) payload.workflowType = allowedWorkflowType;
      if (allowEmptyVerifyResponse === true) {
        payload.allowEmptyVerifyResponse = true;
      }

      const response = await axios.post(
        '/api/auth/register/otp/verify',
        payload,
        {
          withCredentials: true,
          headers: {
            ...identityAuthCodeFlowConfig.headers,
            'Content-Type': 'application/json',
          },
        },
      );

      return response.data;
    } catch (err) {
      newRelic.noticeError(err);
      throw err;
    }
  },

  async registerUser(firstName, lastName, email, password, phone, workflowType) {
    try {
      const body = {
        firstName,
        lastName,
        email,
        password,
        phone,
      };
      const allowedWorkflowType = allowedRegistrationWorkflowType(workflowType);
      if (allowedWorkflowType) body.workflowType = allowedWorkflowType;

      const response = await axios.post(
        '/api/auth/signup',
        body,
        {
          headers: {
            ...identityAuthCodeFlowConfig.headers,
            'Content-Type': 'application/json',
          },
          withCredentials: true,
        },
      );

      return response.data;
    } catch (err) {
      newRelic.noticeError(err);
      throw err;
    }
  },

  async linkUser(hstreamId, email, queryString) {
    try {
      const response = await axios.post(
        `/v2/account/link?${queryString}`,
        {
          hstreamId,
          email,
        },
        {
          headers: {
            ...identityAuthCodeFlowConfig.headers,
          },
          withCredentials: true,
        },
      );

      return response.data;
    } catch (err) {
      newRelic.noticeError(err);
      throw err;
    }
  },

  async oauthLogin(username, password, queryString) {
    try {
      const resp = await axios.post(
        `/v2/oauth2/Login${queryString}`,
        qs.stringify({
          username,
          password,
        }),
        {
          ...identityAuthCodeFlowConfig,
          ...{
            withCredentials: true,
            headers: {
              ...identityAuthCodeFlowConfig.headers,
              'Content-Type': 'application/x-www-form-urlencoded',
            },
          },
        },
      );

      if (resp.headers.location) {
        localStorage.setItem('isMobilePrompt', 1);
        localStorage.setItem('oAuthRedirectUrl', resp.headers.location);
      }
      return resp.data;
    } catch (err) {
      throw err;
    }
  },

  async SendOtp(hstreamId, email, phone, verificationMethod, queryString) {
    try {
      const resp = await axios.post(
        `/api/auth/otp/send${queryString}`,
        {
          message: {
            hstreamId,
            email,
            phone,
            verificationMethod,
          },
        },
        identityClientConfig,
      );
    } catch (err) {
      throw err;
    }
  },

  async ConfirmOtp(hstreamId, verification, queryString) {
    const payload = {
      hstreamId: hstreamId,
      code: verification,
    };
    try {
      const resp = await axios.post(
        `/api/auth/otp/verify${queryString}`,
        JSON.stringify(payload),
        identityClientConfig,
      );
      if (resp.headers.location) {
        localStorage.setItem('isMobilePrompt', 1);
        localStorage.setItem('oAuthRedirectUrl', resp.headers.location);
      }
      return;
    } catch (err) {
      throw err;
    }
  },

  /**
   * POST /api/{hstreamId}/setalias with body { type, value }.
   * @param {string} hstreamId
   * @param {'email'|'phone'} type
   * @param {string} value
   */
  async SetAlias(hstreamId, type, value) {
    try {
      const resp = await axios.post(
        `/api/${hstreamId}/setalias`,
        {
          type,
          value,
        },
        identityClientConfig,
      );
      return resp.data;
    } catch (err) {
      newRelic.noticeError(err);
      throw err;
    }
  },

  /**
   * POST /api/setAdminPasswordandPrimaryLogin
   * Sets Cognito/IDP password and promotes selected email/phone to primary login.
   * Only selected contacts are sent; other verified contacts remain secondary.
   */
  async setAdminPasswordAndPrimaryLogin({
    hStreamId,
    password,
    selectedPrimaryEmail = '',
    selectedPrimaryPhone = '',
    registrationEmail = '',
    workflowType = 'NewRegistration',
    isProvisioned = false,
    isConfirmed = false,
  }) {
    const payload = {
      hStreamId,
      workflowType:
        allowedRegistrationWorkflowType(workflowType) ||
        WORKFLOW_TYPE_REGISTRATION,
      isProvisioned,
      isConfirmed,
    };
    if (password) {
      payload.password = password;
    }
    if (selectedPrimaryEmail) {
      payload.selectedPrimaryEmail = selectedPrimaryEmail;
    }
    if (selectedPrimaryPhone) {
      payload.selectedPrimaryPhone = selectedPrimaryPhone;
    }
    if (registrationEmail) {
      payload.registrationEmail = registrationEmail;
    }

    try {
      const resp = await axios.post(
        '/api/setAdminPasswordandPrimaryLogin',
        payload,
        identityClientConfig,
      );
      return resp.data;
    } catch (err) {
      newRelic.noticeError(err);
      throw err;
    }
  },

  async fetchGetApplicationsData(clientId) {
    try {
      const resp = await axios.get(
        `/api/applications/${clientId}`,
        identityClientConfig,
      );
      return resp.data;
    } catch (err) {
      newRelic.noticeError(err);
      throw err;
    }
  },

  async recoverPrimaryEmail(email) {
    try {
      const response = await axios.get(
        `/v2/Oauth2/recoveraccount?secondaryContact=${email}`,
        { ...identityAuthCodeFlowConfig },
      );
      return response.data;
    } catch (err) {
      newRelic.noticeError(err);
      throw err;
    }
  },

  //this will call getCurrentSession Api method to check csrf cookie session
  async getCurrentSession() {
    try {
      const resp = await axios.get(`/v2/Oauth2/getCurrentSession`, {
        ...{
          withCredentials: true,
          headers: {
            ...identityAuthCodeFlowConfig.headers,
            ...{ 'Content-Type': 'application/x-www-form-urlencoded' },
          },
        },
        ...identityAuthCodeFlowConfig,
      });
      localStorage.setItem('accessTokenId', resp.data.accessToken);
      return resp;
    } catch (err) {
      newRelic.noticeError(err);
      throw err;
    }
  },

  //this will call logOut Api method to get the current user logged out and will clear current user session
  async logOut(response_type, client_id) {
    try {
      let endpointUrl = `/v2/Oauth2/logout?response_type=access`;
      if (
        client_id != null &&
        client_id != undefined &&
        response_type != null &&
        response_type != undefined
      ) {
        endpointUrl = `/v2/Oauth2/logout?response_type=${response_type}&client_id=${client_id}`;
      } else if (client_id != null && client_id != undefined) {
        endpointUrl = `/v2/Oauth2/logout?client_id=${client_id}`;
      } else if (response_type != null && response_type != undefined) {
        endpointUrl = `/v2/Oauth2/logout?response_type=${response_type}`;
      }
      const resp = await axios.get(endpointUrl, {
        ...identityAuthCodeFlowConfig,
        ...{
          withCredentials: true,
          headers: { ...identityAuthCodeFlowConfig.headers },
        },
      });
      return resp;
    } catch (err) {
      throw err;
    }
  },

  async oAuthClientSignOut(logout_uri, client_id) {
    try {
      let endpointUrl = `/v2/Oauth2/logout?client_id=${client_id}`;
      if (logout_uri)
        endpointUrl = `/v2/Oauth2/logout?client_id=${client_id}&logout_uri=${logout_uri}`;
      const response = await axios.get(endpointUrl, {
        ...{
          withCredentials: true,
          headers: {
            ...identityAuthCodeFlowConfig.headers,
            ...{ 'Content-Type': 'application/x-www-form-urlencoded' },
          },
        },
        ...identityAuthCodeFlowConfig,
      });
      return response;
    } catch (err) {
      throw err;
    }
  },
  async getTenantConfig(appId, tenantId) {
    try {
      let url = `/api/configuration?appId=${appId}&tenantId=${tenantId}`;
      const response = await axios.get(url, identityClientConfig);
      return response.data;
    } catch (err) {
      newRelic.noticeError(err);
      throw err;
    }
  },
};

export default identityClient;

export { identityClientConfig, identityAuthCodeFlowConfig };
